import { ElementState, ConfiguratorUIElementStates, ContainerStateBase } from '@/elements/base/ElementState';
import { ICompositeProductApi } from '@canvas-logic/clp-sdk';
import {
  IUICfgAttributesContainerElementOptions,
  IUICfgComponentContainerElementOptions,
  IUiCfgContainer,
  IUICfgProductsContainerElementOptions,
} from '@canvas-logic/clp-api-types';
import React from 'react';

type IUIElementConfig<TViewElement = any> = {
  init: (config: {
    api: ICompositeProductApi;
    viewElement: TViewElement;
    componentSettings: IUICfgComponentContainerElementOptions;
    productsSettings: IUICfgProductsContainerElementOptions;
    attributeSettings: IUICfgAttributesContainerElementOptions;
  }) => ElementState;
  Component: React.ComponentType<{ state: any }>;
};

type IUIContainerElementConfig = {
  init: (config: {
    api: ICompositeProductApi;
    viewElement: IUiCfgContainer;
    children: Array<ConfiguratorUIElementStates>;
  }) => ContainerStateBase;
  Component: React.ComponentType<{
    viewElement: any;
    childrenItems: ConfiguratorUIElementStates[];
  }>;
};

const UI_ELEMENTS: Map<string, IUIElementConfig> = new Map();
const CONTAINERS_UI_ELEMENTS: Map<string, IUIContainerElementConfig> = new Map();

export const registerUIElement = <TViewElement>(kind: string, type: string, config: IUIElementConfig<TViewElement>) => {
  const key = kind + '::' + type;
  if (UI_ELEMENTS.has(key)) {
    throw new Error(`Element for kind ${kind} and type ${type} is already registered`);
  }
  if (CONTAINERS_UI_ELEMENTS.has(key)) {
    throw new Error(
      `Element type with the same kind ${kind} and type ${type} is already registered in Containers UI elements`,
    );
  }
  UI_ELEMENTS.set(key, config);
};

export const registerContainerUIElement = (kind: string, type: string, config: IUIContainerElementConfig) => {
  const key = kind + '::' + type;
  if (CONTAINERS_UI_ELEMENTS.has(key)) {
    throw new Error(`Element for kind ${kind} and type ${type} is already registered`);
  }
  if (UI_ELEMENTS.has(key)) {
    throw new Error(
      `Element type with the same kind ${kind} and type ${type} is already registered in Containers UI elements`,
    );
  }
  CONTAINERS_UI_ELEMENTS.set(key, config);
};

export const getUIElement = (kind: string, elementType: string): IUIElementConfig => {
  const key = kind + '::' + elementType;
  const config = UI_ELEMENTS.get(key);
  if (!config) {
    throw new Error(`Element of kind ${kind} and type ${elementType}  is not registered`);
  }
  return config;
};

export const getContainerUIElement = (kind: string, elementType: string): IUIContainerElementConfig => {
  const key = kind + '::' + elementType;
  const config = CONTAINERS_UI_ELEMENTS.get(key);
  if (!config) {
    throw new Error(`Element ${key} is not registered`);
  }
  return config;
};

export const getUIElementComponent = (kind: string, elementType: string): React.ComponentType<{ state: any }> => {
  return getUIElement(kind, elementType).Component;
};

export const getContainerUIElementComponent = (
  kind: string,
  elementType: string,
): React.ComponentType<{ viewElement: any; childrenItems: ConfiguratorUIElementStates[] }> => {
  return getContainerUIElement(kind, elementType).Component;
};
